import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message, Row, Col, Switch, DatePicker, Upload, Button, Radio } from "antd";
import { UploadOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import OfferServ from "../service/OfferServ";
import GlobalButton from "../util/GlobalButton";

const { Option } = Select;
const { TextArea } = Input;

const AddOffer = ({ visible, offerData, editing, onCancel, onSuccess, users }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [imageSource, setImageSource] = useState('upload');
    const [uploading, setUploading] = useState(false);

    const isActive = Form.useWatch('isActive', form);

    useEffect(() => {
        if (editing && offerData) {
            form.setFieldsValue({
                ...offerData,
                expiryDate: offerData.expiryDate ? dayjs(offerData.expiryDate) : undefined,
            });
            setImageUrl(offerData.imageUrl || null);
            // Detect if existing image is a URL or uploaded path
            if (offerData.imageUrl && (offerData.imageUrl.startsWith('http') || offerData.imageUrl.startsWith('https'))) {
                setImageSource('url');
            } else {
                setImageSource('upload');
            }
        } else {
            form.resetFields();
            form.setFieldsValue({ isActive: true });
            setImageUrl(null);
            setImageSource('upload');
        }
    }, [editing, offerData, form]);

    const handleImageUpload = async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        try {
            setUploading(true);
            const response = await OfferServ.uploadImage(formData);
            const uploadedUrl = response.imageUrl || response.data?.imageUrl;
            setImageUrl(uploadedUrl);
            message.success("Image uploaded successfully");
        } catch (error) {
            console.error("Error uploading image:", error);
            message.error("Failed to upload image");
        } finally {
            setUploading(false);
        }
        return false;
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const payload = {
                ...values,
                expiryDate: values.expiryDate ? values.expiryDate.toISOString() : undefined,
                imageUrl: imageUrl || undefined,
            };

            if (editing) {
                await OfferServ.update(offerData._id, payload);
                message.success("Offer updated successfully");
            } else {
                await OfferServ.create(payload);
                message.success("Offer created successfully");
            }
            onSuccess();
        } catch (error) {
            console.error("Error saving offer:", error);
            message.error(error.message || "Failed to save offer");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <h2 className="nz-modal-title">
                    {editing ? "Edit Offer" : "Create New Offer"}
                </h2>
            }
            open={visible}
            onCancel={onCancel}
            className="custom-modal"
            footer={
                <div className="nz-form-footer">
                    <GlobalButton action="cancel" size="small" onClick={onCancel} disabled={loading}>
                        Cancel
                    </GlobalButton>
                    <GlobalButton action="submit" size="small" loading={loading} onClick={() => form.submit()}>
                        {editing ? "Update" : "Save"}
                    </GlobalButton>
                </div>
            }
            width={560}
            centered
        >
            <Form
                form={form}
                layout="vertical"
                size="small"
                requiredMark={false}
                onFinish={onFinish}
                initialValues={{ isActive: true }}
            >
                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            name="title"
                            label={<span className="nz-form-label">Offer Title</span>}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 6 }}
                        >
                            <Input size="small" placeholder="E.g. Special Discount for You!" />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="description"
                            label={<span className="nz-form-label">Description</span>}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 6 }}
                        >
                            <TextArea placeholder="Describe the offer details..." rows={3} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="discount"
                            label={<span className="nz-form-label">Discount (optional)</span>}
                            style={{ marginBottom: 6 }}
                        >
                            <Input size="small" placeholder="E.g. 10% off, Free shipping" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="expiryDate"
                            label={<span className="nz-form-label">Expiry Date</span>}
                            style={{ marginBottom: 6 }}
                        >
                            <DatePicker
                                className="pcu-w-100"
                                size="small"
                                placeholder="Select date"
                                format="DD/MM/YYYY"
                                disabledDate={(current) =>
                                    current && current < dayjs().startOf("day")
                                }
                                popupClassName="centered-calendar-popup"
                                getPopupContainer={() => document.body}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="isActive"
                            label={<span className="nz-form-label">Status</span>}
                            valuePropName="checked"
                            style={{ marginBottom: 6 }}
                        >
                            <div className="nz-status-toggle">
                                <Switch size="small" checkedChildren="ON" unCheckedChildren="OFF" />
                                <span className={`nz-status-text ${isActive ? 'active' : 'inactive'}`}>
                                    {isActive ? 'ACT' : 'OFF'}
                                </span>
                            </div>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            label={<span className="nz-form-label">Offer Image (Optional)</span>}
                            style={{ marginBottom: 8 }}
                        >
                            {/* Source toggle */}
                            <div style={{ marginBottom: 6 }}>
                                <Radio.Group
                                    size="small"
                                    value={imageSource}
                                    onChange={(e) => {
                                        setImageSource(e.target.value);
                                        if (e.target.value === 'upload') {
                                            setImageUrl(null);
                                        }
                                    }}
                                >
                                    <Radio value="upload">
                                        <span style={{ fontSize: '11px', fontWeight: 500 }}>Upload</span>
                                    </Radio>
                                    <Radio value="url">
                                        <span style={{ fontSize: '11px', fontWeight: 500 }}>Image URL</span>
                                    </Radio>
                                </Radio.Group>
                            </div>

                            {imageSource === 'upload' ? (
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    {imageUrl && (
                                        <img
                                            src={imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'https://api.nearzon.com'}${imageUrl}`}
                                            alt="Offer preview"
                                            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
                                        />
                                    )}
                                    <Upload
                                        beforeUpload={handleImageUpload}
                                        maxCount={1}
                                        accept="image/*"
                                        showUploadList={false}
                                    >
                                        <Button size="small" icon={<UploadOutlined />} loading={uploading} style={{ fontSize: '11px', borderRadius: '4px' }}>
                                            {imageUrl ? 'Change Image' : 'Select File'}
                                        </Button>
                                    </Upload>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {imageUrl && (
                                        <img
                                            src={imageUrl}
                                            alt="Offer preview"
                                            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    )}
                                    <Input
                                        size="small"
                                        placeholder="Paste image URL here..."
                                        prefix={<LinkOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
                                        value={imageUrl || ''}
                                        onChange={(e) => setImageUrl(e.target.value)}
                                        style={{ flex: 1, borderRadius: '4px' }}
                                    />
                                </div>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default AddOffer;
